/**
 * Farmland's Pending queue read (FLF-S10, R69): every COA PDF Farmland's integration person's own system has
 * pushed that a QA reviewer has not yet opened, oldest received first.
 *
 * Follows `lotsOnHoldRelease.ts`'s own exact shape: a named function plus a query-key factory, typed off
 * `./generated/coaApi.js`. The push itself (`POST /v1/coa/pending-reviews`) is machine-callable only
 * (`RequireAppToken()`, R63's own auth pattern) and is Farmland's integration system's own concern per the
 * locked design; nothing in this module's frontend ever calls it.
 */
import { coaListParams } from './coaLists.js';
/**
 * Query keys as a factory rather than as literals at each call site, on `lotsOnHoldRelease.ts`'s own
 * convention: a small factory local to this file, since it shares no dependency with `coaReviews.ts` beyond
 * the shared {@link CoaTransport} type.
 */
export const coaPendingReviewQueryKeys = {
    pendingReviews: () => ['module', 'coa', 'pending-reviews'],
    /** The key the Pending page's interactive list caches each view under: a sub-key of `pendingReviews`, so a write that invalidates that refreshes every view and the overview badge. */
    pendingReviewList: () => ['module', 'coa', 'pending-reviews', 'list'],
    pendingReview: (id) => ['module', 'coa', 'pending-reviews', id],
};
/**
 * Lists the pending COA reviews not yet consumed by a persist. With no `query` it asks for the default view, exactly as it
 * always has: the oldest received first, with no query string. With one it asks for a page on the shared list contract.
 */
export function listPendingCoaReviews(transport, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/pending-reviews',
        ...coaListParams(options?.query),
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/** The pending-reviews list, shaped for TanStack Query, so a component passes one object to `useQuery`. */
export function pendingCoaReviewsQueryOptions(transport) {
    return {
        queryKey: coaPendingReviewQueryKeys.pendingReviews(),
        queryFn: (context) => listPendingCoaReviews(transport, { signal: context.signal }),
    };
}
/**
 * Reads one pending COA review, whatever its own consumption state: the launch page opening it needs to
 * answer even after the entry has already dropped off the list (persist consumes it before this read ever
 * runs, per the locked design's own Decision 1).
 */
export function getPendingCoaReview(transport, id, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/pending-reviews/{id}',
        params: { path: { id } },
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/**
 * Deletes one unconsumed pending COA review for this tenant. The API is the authority on whether this is
 * allowed: it refuses an entry a persist has already consumed with a 409, never a silent no-op.
 */
export function deletePendingCoaReview(transport, id) {
    return transport.send({
        method: 'DELETE',
        path: '/v1/coa/pending-reviews/{id}',
        params: { path: { id } },
    });
}
/** One pending COA review's own read, shaped for TanStack Query. */
export function pendingCoaReviewQueryOptions(transport, id) {
    return {
        queryKey: coaPendingReviewQueryKeys.pendingReview(id),
        queryFn: (context) => getPendingCoaReview(transport, id, { signal: context.signal }),
    };
}
//# sourceMappingURL=coaPendingReviews.js.map